import React, { useEffect, useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Save, Upload, Loader2 } from "lucide-react";

const GROUPS = [
  { title: "Homepage — Hero", fields: [
    { k: "home_hero_eyebrow", l: "Eyebrow", t: "text", d: "International child voice casting" },
    { k: "home_hero_line1", l: "Title line 1", t: "text", d: "Real kids." },
    { k: "home_hero_line2", l: "Title line 2", t: "text", d: "Real voices." },
    { k: "home_hero_line3", l: "Title line 3 (accent)", t: "text", d: "Worldwide." },
    { k: "home_hero_subtitle", l: "Subtitle", t: "textarea", d: "Professional child voice actors in 30+ languages. Find, listen, shortlist and cast the perfect young voice for your production." },
    { k: "home_hero_cta1", l: "Primary button", t: "text", d: "Find a voice" },
    { k: "home_hero_cta2", l: "Secondary button", t: "text", d: "Let us cast for you" },
    { k: "home_hero_link", l: "AI link", t: "text", d: "Try AI voices instantly" },
  ] },
  { title: "Homepage — Section headings", fields: [
    { k: "home_featured_eyebrow", l: "Featured eyebrow", t: "text", d: "Featured voices" },
    { k: "home_featured_title", l: "Featured title", t: "text", d: "Hear the talent" },
    { k: "home_brands_title", l: "Brands strip title", t: "text", d: "Trusted by brands worldwide" },
  ] },
  { title: "About page", fields: [
    { k: "about_title", l: "Heading", t: "text", d: "The specialist in children's voices" },
    { k: "about_p1", l: "Paragraph 1", t: "textarea", d: "" },
    { k: "about_p2", l: "Paragraph 2", t: "textarea", d: "" },
    { k: "about_image", l: "Photo", t: "image", d: "" },
  ] },
  { title: "Studio page", fields: [
    { k: "studio_intro", l: "Intro paragraph", t: "textarea", d: "A dedicated in-house recording studio in the Netherlands, plus partner studios worldwide. Every child is recorded with a specialist director — parents or caregivers always present." },
    { k: "studio_equipment", l: "Equipment list (one item per line)", t: "textarea", d: "Neumann U87\nSennheiser MKH416\nTrident S80 pre-amp\nADL1000 tube compressor\nSSL G-channel\nDBX160\nQuested / KRK / Avantone monitors\nPro Tools 2020.5\nFocusrite Scarlett 18i20\nStudioBricks vocal booth" },
    { k: "studio_image", l: "Studio photo (between sections)", t: "image", d: "" },
  ] },
  { title: "Contact page", fields: [
    { k: "contact_email", l: "Email", t: "text", d: "bookings@voiceoverkids.agency" },
    { k: "contact_phone", l: "Phone", t: "text", d: "+31 71 2032441" },
    { k: "contact_address", l: "Address", t: "textarea", d: "VoiceOverKids.Agency\nDe Savornin Lohmanplein 2\n2314 EV Leiden, The Netherlands" },
  ] },
  { title: "Podcast page", fields: [
    { k: "podcast_title", l: "Heading", t: "text", d: "The VoiceOverKids Podcast" },
    { k: "podcast_intro", l: "Intro text", t: "textarea", d: "" },
    { k: "podcast_episodes", l: "Episodes", t: "episodes" },
    { k: "podcast_sidebar_title", l: "Side panel heading (beside episodes)", t: "text", d: "About the show" },
    { k: "podcast_sidebar", l: "Side panel text (beside episodes)", t: "textarea", d: "" },
  ] },
  { title: "Social media links (header icons)", fields: [
    { k: "social_tiktok", l: "TikTok URL", t: "text", d: "https://www.tiktok.com/@..." },
    { k: "social_instagram", l: "Instagram URL", t: "text", d: "https://www.instagram.com/..." },
    { k: "social_facebook", l: "Facebook URL", t: "text", d: "https://www.facebook.com/..." },
    { k: "social_youtube", l: "YouTube URL", t: "text", d: "https://www.youtube.com/@voiceoverkidsagency" },
    { k: "social_spotify", l: "Spotify URL", t: "text", d: "https://open.spotify.com/..." },
  ] },
];

export default function AdminContent() {
  const [vals, setVals] = useState({});
  const [episodes, setEpisodes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const refs = useRef({});

  useEffect(() => {
    api.get("/admin/content").then((r) => {
      const data = r.data || {};
      setVals(data);
      try { const a = JSON.parse(data.podcast_episodes || "[]"); if (Array.isArray(a)) setEpisodes(a.map((e) => ({ url: e.url || "", desc: e.desc || "" }))); }
      catch { setEpisodes([]); }
    }).catch(() => {});
  }, []);
  const set = (k, v) => setVals((p) => ({ ...p, [k]: v }));
  const updateEpisodes = (arr) => { setEpisodes(arr); set("podcast_episodes", JSON.stringify(arr)); };

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/content", { updates: vals }); toast.success("Saved — changes are live on the site"); }
    catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const upload = async (k, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(k);
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "content");
    try { const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set(k, data.url); toast.success("Uploaded"); }
    catch (err) { toast.error("Upload failed"); } finally { setBusy(null); e.target.value = ""; }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold">Pages content</h1><p className="mt-1 text-sm text-zinc-500">Edit the text and images shown on your website. Leave a field blank to use the default.</p></div>
        <button onClick={save} disabled={saving} data-testid="save-content" className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-6 py-2.5 text-sm font-bold text-white hover:bg-brand-violetHover disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save all
        </button>
      </div>

      {GROUPS.map((g) => (
        <div key={g.title} className="mt-5 rounded-xl border border-zinc-200 bg-white p-5">
          <h2 className="font-heading font-bold">{g.title}</h2>
          <div className="mt-3 space-y-4">
            {g.fields.map((f) => (
              <div key={f.k}>
                <Label className="label-eyebrow text-zinc-500">{f.l}</Label>
                {f.t === "episodes" ? (
                  <div className="mt-1 space-y-3" data-testid="episodes-editor">
                    {episodes.map((ep, i) => (
                      <div key={i} className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-xs font-bold uppercase tracking-wide text-zinc-500">Episode {i + 1}</span>
                          <button type="button" onClick={() => updateEpisodes(episodes.filter((_, x) => x !== i))} data-testid={`ep-remove-${i}`} className="text-xs font-semibold text-red-500 hover:underline">Remove</button>
                        </div>
                        <Input value={ep.url} onChange={(e) => { const n = [...episodes]; n[i] = { ...n[i], url: e.target.value }; updateEpisodes(n); }}
                          placeholder="YouTube link (e.g. https://youtu.be/VIDEO_ID)" data-testid={`ep-url-${i}`} className="h-10 rounded-lg" />
                        <Textarea value={ep.desc} onChange={(e) => { const n = [...episodes]; n[i] = { ...n[i], desc: e.target.value }; updateEpisodes(n); }}
                          placeholder="Episode description" rows={3} data-testid={`ep-desc-${i}`} className="mt-2 rounded-lg" />
                      </div>
                    ))}
                    <button type="button" onClick={() => updateEpisodes([...episodes, { url: "", desc: "" }])} data-testid="add-episode"
                      className="w-full rounded-lg border border-dashed border-zinc-300 px-3 py-2.5 text-sm font-semibold text-zinc-600 transition hover:border-brand-violet hover:text-brand-violet">
                      + Add episode
                    </button>
                  </div>
                ) : f.t === "image" ? (
                  <div className="mt-1 flex items-center gap-3">
                    <div className="h-16 w-24 overflow-hidden rounded-lg bg-zinc-100">{vals[f.k] && <img src={mediaUrl(vals[f.k])} alt="" className="h-full w-full object-cover" />}</div>
                    <input ref={(el) => (refs.current[f.k] = el)} type="file" accept="image/*" hidden onChange={(e) => upload(f.k, e)} data-testid={`content-img-${f.k}`} />
                    <button onClick={() => refs.current[f.k]?.click()} disabled={busy === f.k} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 px-3 py-2 text-xs font-semibold hover:border-brand-violet">
                      {busy === f.k ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
                    </button>
                  </div>
                ) : f.t === "textarea" ? (
                  <Textarea value={vals[f.k] ?? ""} onChange={(e) => set(f.k, e.target.value)} placeholder={f.d} rows={3} data-testid={`content-${f.k}`} className="mt-1 rounded-lg" />
                ) : (
                  <Input value={vals[f.k] ?? ""} onChange={(e) => set(f.k, e.target.value)} placeholder={f.d} data-testid={`content-${f.k}`} className="mt-1 h-10 rounded-lg" />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
